'use strict';

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import FoodIngredients from '@components/Modules/FoodIngredients.react';
import MealEditControls from '../../actions/MealEditControls.react';
import Alert from '@components/Widgets/Alert/Alert.react';
import LocationInfo from '@components/Products/LocationInfo.react';
import RecipeInstructions from '@components/Modules/RecipeInstructions.react';

import { useAssets, usePermissions } from '../../contexts';
import { isSunbasketFood } from '@utils/Sunbasket';
import UserStore from '@stores/UserStore';

/**
 * Helper function to calculate estimated price per serving for foods
 */
const getEstimatedPrice = (item) => {
    if (!item) return null;

    // For foods, only use mapped_from price information
    if (item.mapped_from?.length && item.servings) {
        const mappedWithPrice = item.mapped_from.find((mf) => mf.price);
        if (mappedWithPrice) {
            return mappedWithPrice.price / item.servings;
        }
    }

    return null;
};

/**
 * Helper function to get dish title based on food type
 */
const getDishTitle = (food) => {
    const dishTitles = {
        'Restaurant Dish': (
            <>
                <span>Order from</span> <em>{food.brand_name}</em>
            </>
        ),
        'Ready Made Meal': food.brand_name ? (
            <>
                <span>Ready Made Meal by</span> <em>{food.brand_name}</em>
            </>
        ) : (
            'Ready Made Meal'
        ),
        'Brand Name Food': <span>Store Bought Item</span>,
        Food: <></>,
        UGC: <span>Custom Food</span>,
        'Meal Kit': food.brand_name ? (
            <>
                <span>Meal kit by</span> <em>{food.brand_name}</em>
            </>
        ) : (
            'Meal kit'
        ),
    };
    
    return dishTitles[food.type] || <></>;
};

/**
 * FoodContentDisplay - Displays food content section
 * 
 * Extracted from MealDetails to improve component organization and testability
 */
const FoodContentDisplay = ({
    meal,
    food,
    profile,
    sortedMeals,
    dishIndex
}) => {
    // Context hooks
    const { locations, brands } = useAssets();
    const { isPro } = usePermissions();
    
    // Get current user location for LocationInfo
    const currentLocation = UserStore.getLocation();
    
    // Get location and brand information
    const location = (locations && locations[meal.location_uuid]) || null;
    const brand = (food.brand_uuid && brands && brands[food.brand_uuid]) || null;
    
    // Define section class for consistency
    const foodSection = 'first';

    return (
        <div className={classNames('details-section', foodSection)}>
            <header>
                <div className="section-title">
                    <p className="t2">
                        {sortedMeals.length > 1 ? `DISH ${dishIndex + 1}: ` : null}
                        {getDishTitle(food)}
                    </p>
                </div>
                <MealEditControls
                    meal={meal}
                    content={food}
                    sortedMeals={sortedMeals}
                />
            </header>
            <div className="section-body">
                <div className="meal-title-container">
                    <div className="meal-title-content">
                        <h3 className="meal-title" data-testid="meal-title">
                            {food.pretty_name || food.name}
                        </h3>
                    </div>

                    {(() => {
                        const estimatedPrice = getEstimatedPrice(food);
                        return estimatedPrice ? (
                            <div className="price-per-serving">
                                <p className="t4">${estimatedPrice.toFixed(2)}/SERVING EST</p>
                            </div>
                        ) : null;
                    })()}
                </div>

                {food.description && (
                    <p className="t3 description">{food.description}</p>
                )}

                {food.ingredients && (
                    <>
                        <p className="p3">This meal includes the following ingredients:</p>
                        <FoodIngredients food={food} profile={profile} />
                    </>
                )}

                {food.modifications && (
                    <p className="t3 modifications">
                        <em>Recommended modifications:</em> {food.modifications}
                    </p>
                )}

                {/* Nutrition Disclaimer */}
                {food.nutrition_disclaimer && (
                    <Alert type="warning" title="Nutrition Disclaimer">
                        {food.nutrition_disclaimer}
                    </Alert>
                )}

                {/* Barcode Display */}
                {food.gtin_upc?.length > 0 && (
                    <p className="t3">
                        <em>Barcode:</em> {food.gtin_upc.join(', ')}
                    </p>
                )}

                {/* Sunbasket-Specific Alert */}
                {isPro && isSunbasketFood(food) && (
                    <Alert type="question" iconType="feather feather-alert-circle">
                        <p className="t3">
                            <strong>This is a sample Sunbasket meal.</strong>
                            <br /><br />
                        </p>
                        <p className="t3">
                            This exact meal may or may not be available in your{' '}
                            {profile.practice_type === 'dietetics' ? "patient's" : "client's"} area...
                        </p>
                    </Alert>
                )}
            </div>

            {/* Restaurant Information Section */}
            {location && (
                <div className={classNames('details-section', 'second')}>
                    <header>
                        <div className="section-title">
                            <p className="t2">Restaurant Information</p>
                        </div>
                    </header>
                    <div className="section-body">
                        <LocationInfo 
                            brand={brand} 
                            location={location} 
                            currentLocation={currentLocation} 
                        />
                    </div>
                </div>
            )}

            {/* Food Preparation Instructions Section */}
            {food.preparation && (
                <div className={classNames('details-section', 'third')}>
                    <header>
                        <div className="section-title">
                            <p className="t2">
                                Dish {sortedMeals.length === 1 ? null : dishIndex + 1} Instructions
                            </p>
                        </div>
                    </header>
                    <div className="section-body">
                        <RecipeInstructions 
                            recipe={food} 
                            details={food} 
                            user={profile} 
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

FoodContentDisplay.propTypes = {
    meal: PropTypes.object.isRequired,
    food: PropTypes.object.isRequired,
    profile: PropTypes.object.isRequired,
    sortedMeals: PropTypes.array.isRequired,
    dishIndex: PropTypes.number.isRequired,
};

export default FoodContentDisplay;